import { profileCode, catalogueFor, userSeriesForTypology } from "@/lib/catalogue";
import { coupledJoints } from "@/lib/joints";
import { activeCorners, isCorner } from "@/lib/geometry";

const GASKET = "#111111", SEAL = "#2B2B2B", PACKER = "#9CA3AF", ALU = "#D9DEE4", GLASS = "#DCEFFA", SPACER = "#A3A3A3", WALLF = "#F5F5F4", PILE = "#6B7280", FOAM = "#FEF3C7", STEEL = "#6B7280", PLUG = "#FDE68A";

const DXF_LAYER = { [GASKET]: "GASKET", [SEAL]: "SEALANT", [PACKER]: "PACKER", [ALU]: "ALU_PROFILE", [GLASS]: "GLASS", [SPACER]: "GLASS_SPACER", [WALLF]: "WALL", [PILE]: "WEATHER_PILE", [FOAM]: "PU_FOAM", [STEEL]: "ANCHOR", [PLUG]: "ANCHOR", "#FFFFFF": "BACKER_ROD", "#4B5563": "SETTING_BLOCK", "#9CA3AF": "ROLLER" };

export function parseGlass(spec) {
  const s = (spec || "").toLowerCase();
  const nums = [...s.matchAll(/(\d+(?:\.\d+)?)\s*(mm|a)\b/g)].map((m) => ({ v: +m[1], gap: m[2] === "a" }));
  const panes = nums.filter((n) => !n.gap).map((n) => n.v);
  const gap = nums.find((n) => n.gap)?.v;
  if (gap && panes.length >= 2) return { panes: [panes[0], panes[1]], gap, T: panes[0] + gap + panes[1] };
  if (panes.length) return { panes: [panes[0]], gap: 0, T: panes[0] };
  return { panes: [6, 6], gap: 12, T: 24 };
}

function ctx() {
  const P = [], L = [];
  const R = (x0, y0, x1, y1, fill, weight = "thin") => P.push({ t: "rect", x: Math.min(x0, x1), y: Math.min(y0, y1), w: Math.abs(x1 - x0), h: Math.abs(y1 - y0), fill, weight, layer: "SECTION", dxf: DXF_LAYER[fill] || "SECTION" });
  const Ln = (x1, y1, x2, y2, weight = "thin", layer = "SECTION") => P.push({ t: "line", x1, y1, x2, y2, weight, layer });
  const C = (cx, cy, r, fill) => P.push({ t: "circle", cx, cy, r, fill, weight: "thin", layer: "SECTION", dxf: DXF_LAYER[fill] || "SECTION" });
  const Poly = (pts, fill, weight = "thin") => P.push({ t: "poly", pts, fill, weight, layer: "SECTION", dxf: DXF_LAYER[fill] || "SECTION" });
  const lab = (x, y, text) => L.push({ x, y, text });
  return { P, L, R, Ln, C, Poly, lab, used: 0 };
}

function hatch(c, x0, y0, x1, y1, s = 7) {
  const x = Math.min(x0, x1), y = Math.min(y0, y1), w = Math.abs(x1 - x0), h = Math.abs(y1 - y0);
  for (let k = -h; k < w; k += s) {
    const t0 = Math.max(0, -k), t1 = Math.min(h, w - k);
    if (t1 > t0) c.Ln(x + k + t0, y + h - t0, x + k + t1, y + h - t1, "thin", "HATCH");
  }
}

function body(c, x0, y0, x1, y1) {
  c.R(x0, y0, x1, y1, ALU, "medium");
  if (Math.abs(x1 - x0) > 10 && Math.abs(y1 - y0) > 10) {
    const sx = Math.sign(x1 - x0), sy = Math.sign(y1 - y0), mid = (x0 + x1) / 2;
    c.R(x0 + 2 * sx, y0 + 2 * sy, x1 - 2 * sx, y1 - 2 * sy, "#FFFFFF");
    c.R(mid - 1, y0 + 2 * sy, mid + 1, y1 - 2 * sy, ALU);
  }
}

function shapeIn(c, shape, x0, y0, x1, y1) {
  if (!shape) return false;
  const sx = (x1 - x0) / shape.width, sy = (y1 - y0) / shape.height;
  const m = (q) => [x0 + q[0] * sx, y0 + q[1] * sy];
  c.Poly(shape.outline.map(m), ALU, "medium");
  (shape.chambers || []).forEach((q) => c.Poly(q.map(m), "#FFFFFF"));
  (shape.thermal_break || []).forEach((q) => c.Poly(q.map(m), "#374151"));
  c.used++;
  return true;
}

const shapesOf = (item) => catalogueFor(item.profile_system)?.shapes || {};

function fitGlass(glass, avail) {
  return glass.T <= avail ? glass : { panes: [Math.max(5, Math.min(8, avail))], gap: 0, T: Math.max(5, Math.min(8, avail)) };
}

function pocket(c, { x0, x1, yFace, dir, glass, labels = true, setting = false, code = "", noLip = false }) {
  const g = fitGlass(glass, x1 - x0 - 22);
  const T = g.T, b = yFace - 18 * dir, yg = yFace - 14 * dir, yEnd = yFace + 40 * dir;
  if (!noLip) c.R(x0, b, x0 + 4, yFace, ALU, "medium");
  c.R(x0 + 4, yFace - 11 * dir, x0 + 8, yFace - 1 * dir, GASKET, "none");
  const gx = x0 + 8;
  let px = gx;
  g.panes.forEach((t, i) => {
    c.R(px, yg, px + t, yEnd, GLASS);
    px += t;
    if (i === 0 && g.panes.length > 1) {
      c.R(px, yg, px + g.gap, yg + 10 * dir, SPACER);
      c.R(px, yg, px + g.gap, yg + 3 * dir, SEAL, "none");
      px += g.gap;
    }
  });
  c.Ln(gx - 4, yEnd, gx + T + 4, yEnd, "thin", "LEADER");
  c.R(gx + T, yFace - 11 * dir, gx + T + 4, yFace - 1 * dir, GASKET, "none");
  const bx1 = Math.min(x1, gx + T + 16);
  c.R(gx + T + 4, b, bx1, yFace, ALU, "medium");
  if (setting) c.R(gx, b, gx + T, yg, "#4B5563", "none");
  if (labels) {
    c.lab(x0 + 6, yFace - 6 * dir, "EPDM wedge gasket (ext.)");
    c.lab(gx + T + 2, yFace - 4 * dir, "EPDM glazing gasket (int.)");
    c.lab((gx + T + 4 + bx1) / 2, yFace - 14 * dir, `Snap-in glazing bead${code ? ` ${code}` : ""}`);
    c.lab(gx + T / 2, yFace + 28 * dir, g.panes.length > 1 ? `DGU ${g.panes[0]}+${g.gap}A+${g.panes[1]} mm` : `${g.T} mm glass`);
    if (g.panes.length > 1) c.lab(gx + g.panes[0] + g.gap / 2, yg + 7 * dir, "Spacer + secondary sealant");
    if (setting) c.lab(gx + T / 2, (b + yg) / 2, "Setting block (EPDM)");
  }
}

function wall(c, D, floor) {
  const wy = -10;
  c.R(-35, wy - 55, D + 35, wy, WALLF, "medium");
  hatch(c, -35, wy - 55, D + 35, wy);
  c.R(0, wy, 7, 0, SEAL, "none");
  c.C(11.5, wy / 2, 4.5, "#FFFFFF");
  c.R(D - 7, wy, D, 0, SEAL, "none");
  c.C(D - 11.5, wy / 2, 4.5, "#FFFFFF");
  c.R(16, wy, D - 16, 0, FOAM, "none");
  c.R(D / 2 - 16, wy, D / 2 - 2, 0, PACKER);
  const ax = D / 2 + 6;
  c.R(ax - 4, wy - 42, ax + 4, wy - 2, PLUG);
  c.R(ax - 1.6, wy - 45, ax + 1.6, 7, STEEL);
  c.R(ax - 4, 5, ax + 4, 7.5, STEEL);
  c.lab(3.5, wy / 2, "Weather silicone sealant (ext.)");
  c.lab(11.5, wy / 2, "PE backer rod");
  c.lab(D / 2 - 9, wy / 2, "Packer / shim at fixing points");
  c.lab(D / 2 + 14, wy / 2, "PU foam insulation");
  c.lab(ax, wy - 28, "Anchor: Ø6 SS screw + nylon plug ≤500 c/c");
  c.lab(D - 3.5, wy / 2, "Silicone sealant (int.)");
  c.lab(-28, wy - 45, floor ? "Floor slab / finished floor" : "RCC / masonry opening");
}

const kindOf = (ops) => (ops.some((o) => o.startsWith("sliding")) ? "sliding" : ops.some((o) => o.startsWith("door") || o.startsWith("folding")) ? "door" : ops.some((o) => o !== "fixed" && o !== "louvre") ? "sash" : "fixed");

function edgeDetail(item, glass, variant, edge) {
  const c = ctx();
  const pc = (n) => { const k = profileCode(item, n); return k ? ` ${k}` : ""; };
  const bc = profileCode(item, "bead");
  const shp = shapesOf(item);
  const F = item.frame_width, S = item.sash_width, D = Math.max(item.frame_depth || 60, 40);
  const sill = edge === "sill";
  const floor = sill && variant === "door";
  wall(c, D, floor);
  if (variant === "fixed") {
    const sh = shapeIn(c, (sill && shp.sill) || shp.frame, 0, 0, D, Math.max(28, F));
    if (!sh) body(c, 0, 0, D, Math.max(10, F - 18));
    pocket(c, { x0: 0, x1: D, yFace: Math.max(28, F), dir: 1, glass, setting: sill, code: bc, noLip: sh });
    c.lab(D * 0.75, Math.max(10, F - 18) / 2, `Outer frame${pc(sill ? "sill" : "frame")} (fixed glazing)`);
    if (sill) c.lab(2, F - 9, "Drainage / weep slot @ 600 c/c");
  } else if (variant === "sliding") {
    if (!shapeIn(c, (sill && shp.sill) || shp.frame, 0, 0, D, F + 10)) {
      body(c, 0, 0, D, F);
      c.R(0, F, 3, F + 10, ALU, "medium"); c.R(D / 2 - 2, F, D / 2 + 2, F + 10, ALU, "medium"); c.R(D - 3, F, D, F + 10, ALU, "medium");
    }
    [[5, D / 2 - 4], [D / 2 + 4, D - 5]].forEach(([x0, x1], i) => {
      const sb = F + 4 + Math.max(10, S - 18);
      const ssh = shapeIn(c, shp.sash, x0, F + 4, x1, sb + 18);
      if (!ssh) body(c, x0, F + 4, x1, sb);
      c.R(x0 - 1, F, x0 + 1.5, F + 4, PILE, "none"); c.R(x1 - 1.5, F, x1 + 1, F + 4, PILE, "none");
      pocket(c, { x0, x1, yFace: sb + 18, dir: 1, glass, labels: i === 1, setting: sill, noLip: ssh });
      if (sill) c.C((x0 + x1) / 2, F + 2, 2.5, "#9CA3AF");
    });
    c.lab(D * 0.5, F / 2, `Outer frame${pc(sill ? "sill" : "frame")} (2-track)`);
    c.lab(D / 2 - 4, F + 2, "Wool pile weather seal");
    c.lab(D * 0.25, F + 10, `Sliding sash${pc("sash")}`);
    if (sill) c.lab(D * 0.75, F + 2, "Tandem rollers on track");
  } else {
    const fh = floor ? 22 : F;
    if (!shapeIn(c, (sill && shp.sill) || shp.frame, 0, 0, D, fh)) {
      body(c, 0, 0, D, fh);
      c.R(D - 6, fh, D, fh + 10, ALU, "medium");
    }
    const sb = fh + 3 + Math.max(10, S - 18);
    const vsh = shapeIn(c, (variant === "door" && shp.door_leaf) || shp.sash, 3, fh + 3, D - 8, sb + 18);
    if (!vsh) body(c, 3, fh + 3, D - 8, sb);
    c.C(9, fh + 1.5, 1.8, GASKET); c.C(D - 11, fh + 1.5, 1.8, GASKET);
    pocket(c, { x0: 3, x1: D - 8, yFace: sb + 18, dir: 1, glass, setting: sill, code: bc, noLip: vsh });
    c.lab(D * 0.7, fh / 2, floor ? `Low threshold profile${pc("sill")}` : `Outer frame${pc(sill ? "sill" : "frame")}`);
    c.lab(D * 0.35, (fh + 3 + sb) / 2, variant === "door" ? `Door leaf${pc("door leaf")} (stile/rail)` : `Sash${pc("sash")} (vent)`);
    c.lab(9, fh + 1.5, floor ? "Brush / drop seal" : "EPDM bulb seal (ext.)");
    c.lab(D - 11, fh + 1.5, "EPDM bulb seal (int.)");
    if (!sill) c.lab(D / 2, fh + 1.5, variant === "door" ? "Butt hinge / pivot" : "Friction stay / hinge");
  }
  const map = sill ? (x, y) => [x, -y] : edge === "jamb" ? (x, y) => [y, x] : (x, y) => [x, y];
  return finalize(c, map);
}

function mullionDetail(item, glass, horizontal) {
  const c = ctx();
  const M = item.mullion_width, D = Math.max(item.frame_depth || 60, 40), h = Math.max(4, M / 2 - 18);
  const half = Math.max(22, M / 2);
  const sh = shapeIn(c, shapesOf(item).mullion, 0, -half, D, half);
  if (!sh) body(c, 0, -h, D, h);
  pocket(c, { x0: 0, x1: D, yFace: half, dir: 1, glass, noLip: sh });
  pocket(c, { x0: 0, x1: D, yFace: -half, dir: -1, glass, labels: false, noLip: sh });
  c.lab(D * 0.75, 0, horizontal ? `Transom${profileCode(item, "transom") ? ` ${profileCode(item, "transom")}` : " (YKK series)"}` : `Mullion${profileCode(item, "mullion") ? ` ${profileCode(item, "mullion")}` : " (YKK series)"}`);
  c.lab(D * 0.3, 0, "Screw port / shear block joint");
  return finalize(c, horizontal ? (x, y) => [x, y] : (x, y) => [y, x]);
}

function interlockDetail(item, glass) {
  const c = ctx();
  const S = item.sash_width, D = Math.max(item.frame_depth || 70, 50);
  const ish = shapesOf(item).interlock || shapesOf(item).sash;
  const a = shapeIn(c, ish, 5, 0, D / 2 - 4, -S);
  if (!a) body(c, 5, -S + 18, D / 2 - 4, 0);
  pocket(c, { x0: 5, x1: D / 2 - 4, yFace: -S, dir: -1, glass, labels: false, noLip: a });
  const b = shapeIn(c, ish, D / 2 + 4, -10, D - 5, S - 10);
  if (!b) body(c, D / 2 + 4, -10, D - 5, S - 28);
  pocket(c, { x0: D / 2 + 4, x1: D - 5, yFace: S - 10, dir: 1, glass, noLip: b });
  c.R(D / 2 - 4, -10, D / 2 + 4, 0, ALU, "medium");
  c.R(D / 2 - 1, -10, D / 2 + 1, 0, PILE, "none");
  c.lab(D / 2, -5, `Interlock${profileCode(item, "interlock") ? ` ${profileCode(item, "interlock")}` : ""} with wool pile seal`);
  c.lab(D * 0.25, -S / 2, "Sliding sash 1 (meeting stile)");
  c.lab(D * 0.75, S / 2 - 10, "Sliding sash 2 (meeting stile)");
  return finalize(c, (x, y) => [y, x]);
}

const OTHER_TYP = { fixed: "fixed_window", casement: "casement_window", door: "hinged_door_single" };
const BUILTIN = { sliding: "YKK AP IWIN-S Sliding", fixed: "YKK AP IWIN-S Fixed", casement: "YKK AP IWIN-S Casement", door: "YKK AP IWIN-S Casement Door" };

function seriesFor(item, k) {
  const own = /sliding/i.test(item.profile_system || "");
  if ((k === "sliding") === own && item.profile_system) return item.profile_system;
  return userSeriesForTypology(k === "sliding" ? "sliding_window" : OTHER_TYP[k])?.series || BUILTIN[k];
}

function couplingDetail(item, glass, joint) {
  const c = ctx();
  const ko = joint.type.split("+")[1];
  const sA = seriesFor(item, "sliding"), sB = seriesFor(item, ko);
  const A = catalogueFor(sA) || {}, B = catalogueFor(sB) || {};
  const Fa = A.frame || 45, Da = Math.max(A.depth || 75, 50), Ss = A.sash || 60;
  const Fb = B.frame || 50, Db = Math.max(B.depth || 60, 40);
  const Dm = Math.max(Da, Db), g = 6;
  c.R(0, -g, Dm, g, ALU, "medium");
  c.R(-3, -g, 0, g, SEAL, "none"); c.R(Dm, -g, Dm + 3, g, SEAL, "none");
  if (!shapeIn(c, A.shapes?.frame, 0, -g - Fa, Da, -g)) {
    body(c, 0, -g - Fa, Da, -g);
    c.R(Da / 2 - 2, -g - Fa - 10, Da / 2 + 2, -g - Fa, ALU, "medium");
  }
  const sTop = -g - Fa - 3, sFace = sTop - Ss;
  if (!shapeIn(c, A.shapes?.sash, 5, sTop, Da / 2 - 4, sFace)) body(c, 5, sTop, Da / 2 - 4, sFace + 18);
  c.R(4, -g - Fa - 3, 6.5, -g - Fa, PILE, "none");
  pocket(c, { x0: 5, x1: Da / 2 - 4, yFace: sFace, dir: -1, glass, labels: false, noLip: !!A.shapes?.sash });
  const bsh = shapeIn(c, B.shapes?.frame, 0, g, Db, g + Math.max(28, Fb));
  if (!bsh) body(c, 0, g, Db, g + Math.max(10, Fb - 18));
  pocket(c, { x0: 0, x1: Db, yFace: g + Math.max(28, Fb), dir: 1, glass, noLip: bsh });
  [Math.min(Da, Db) * 0.3, Math.min(Da, Db) * 0.72].forEach((bx) => c.R(bx - 1.6, -g - Fa + 4, bx + 1.6, g + Math.max(10, Fb - 18) - 2, STEEL));
  const code = (ser, m) => { const k = catalogueFor(ser)?.codes?.[m]; return k ? ` ${k}` : ""; };
  c.lab(Dm / 2, 0, "Coupling profile / jointing tube");
  c.lab(Math.min(Da, Db) * 0.3, g + 2, "SS through-bolts @ ≤400 c/c");
  c.lab(-1.5, 0, "Weather silicone sealant (ext.)");
  c.lab(Dm + 1.5, 0, "Silicone sealant (int.)");
  c.lab(Da * 0.75, -g - Fa / 2, `Sliding outer frame${code(sA, "frame")} · ${sA.replace(/^YKK AP\s*/, "")}`);
  c.lab(Da * 0.25, sTop - 8, `Sliding sash${code(sA, "sash")}`);
  c.lab(5, -g - Fa - 1.5, "Wool pile seal");
  c.lab(Db * 0.75, g + 6, `${ko === "fixed" ? "Fixed" : ko === "door" ? "Door" : "Casement"} outer frame${code(sB, "frame")} · ${sB.replace(/^YKK AP\s*/, "")}`);
  return { ...finalize(c, joint.orientation === "v" ? (x, y) => [y, x] : (x, y) => [x, y]), series: [sA, sB] };
}

function cornerDetail(item, glass, k) {
  const c = ctx();
  const D = Math.max(item.frame_depth || 60, 40), Pw = Math.max(18, (item.mullion_width || 50) / 2);
  const phi = ((180 - k.angle) * Math.PI) / 180, tn = Math.tan(phi / 2), co = Math.cos(phi), si = Math.sin(phi);
  const A = (s, t) => [-s, -t], B = (s, t) => [s * co - t * si, -s * si - t * co];
  const quad = (F, s0, s1, t0, t1, fill, w = "thin") => c.Poly([F(s0, t0), F(s1, t0), F(s1, t1), F(s0, t1)], fill, w);
  const lab = (pt, text) => c.lab(pt[0], pt[1], text);
  const g = fitGlass(glass, D - 16), tg0 = (D - g.T) / 2, tg1 = tg0 + g.T, L = 95;
  const panes = (F, sOf) => {
    let t = tg0;
    g.panes.forEach((p, i) => {
      c.Poly([F(sOf(t), t), F(L, t), F(L, t + p), F(sOf(t + p), t + p)], GLASS);
      t += p;
      if (i === 0 && g.panes.length > 1) { c.Poly([F(sOf(t), t), F(sOf(t) + 12, t), F(sOf(t + g.gap) + 12, t + g.gap), F(sOf(t + g.gap), t + g.gap)], SPACER); t += g.gap; }
    });
    c.Ln(...F(L, tg0 - 4), ...F(L, tg1 + 4), "thin", "LEADER");
  };
  const outer = k.dir === "int" ? "int." : "ext.", inner = k.dir === "int" ? "ext." : "int.";
  const gl = g.panes.length > 1 ? `DGU ${g.panes[0]}+${g.gap}A+${g.panes[1]} mm` : `${g.T} mm glass`;
  if (k.type === "glass") {
    const sOf = (t) => t * tn + 3 / Math.cos(phi / 2);
    [A, B].forEach((F) => panes(F, sOf));
    c.Poly([A(sOf(tg0), tg0), A(sOf(tg1), tg1), B(sOf(tg1), tg1), B(sOf(tg0), tg0)], SEAL, "none");
    lab(A(sOf((tg0 + tg1) / 2) - 1, (tg0 + tg1) / 2), "Structural silicone joint (UV-stable, black)");
    lab(A(60, (tg0 + tg1) / 2), gl);
    lab(B(40, tg1), "Polished glass edges (DGU: stepped edge seal)");
    lab(A(sOf(tg0), tg0), `Head & sill frames mitred at ${(180 - k.angle) / 2}°`);
  } else {
    const e = D * tn, sE = e + Pw;
    c.Poly([A(sE, 0), A(0, 0), B(sE, 0), B(sE, D), A(e, D), A(sE, D)], ALU, "medium");
    c.Poly([A(sE - 3, 3), A(3 * tn, 3), B(sE - 3, 3), B(sE - 3, D - 3), A((D - 3) * tn, D - 3), A(sE - 3, D - 3)], "#FFFFFF");
    c.Ln(...A(0, 0), ...A(e, D), "medium");
    [A, B].forEach((F) => {
      quad(F, sE, sE + 16, 0, tg0 - 3, ALU, "medium");
      quad(F, sE + 4, sE + 14, tg0 - 3, tg0, GASKET, "none");
      quad(F, sE + 4, sE + 14, tg1, tg1 + 3, GASKET, "none");
      quad(F, sE, sE + 16, tg1 + 3, D, ALU, "medium");
      panes(F, () => sE + 2);
    });
    const code = profileCode(item, "corner") || profileCode(item, "mullion");
    lab(A((e + sE) / 2, D / 2), `${k.tag ? "Corner coupling post" : "Corner post"} ${k.angle}°${code ? ` ${code}` : ""} (aluminium)`);
    lab(A(sE + 9, tg0 - 1.5), `EPDM glazing gasket (${outer})`);
    lab(B(sE + 9, tg1 + 1.5), `EPDM glazing gasket (${inner})`);
    lab(B(sE + 8, (tg1 + 3 + D) / 2), "Snap-in glazing bead");
    lab(A(60, (tg0 + tg1) / 2), gl);
    lab(A(e, D), "Screw ports · head / sill & transom fixing");
    if (k.tag) lab(B(sE / 2, 0), `Through-bolted to ${k.tag} frame, sealed both sides`);
  }
  lab(A(0, 0), `${outer === "ext." ? "Exterior" : "Interior"} face · ${k.angle}° ${k.dir === "int" ? "internal" : "external"} corner`);
  return finalize(c, k.dir === "int" ? (x, y) => [x, -y] : (x, y) => [x, y]);
}

export function cornerSectionDetails(item) {
  const glass = parseGlass(item.glass_spec), seen = new Set(), out = [];
  activeCorners(item).forEach((k) => {
    const key = `${k.angle}-${k.type}-${k.dir}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ title: `${k.type === "glass" ? "GLASS-TO-GLASS CORNER" : "CORNER POST"} ${k.angle}°${k.dir === "int" ? " INTERNAL" : ""} (PLAN)`, ...cornerDetail(item, glass, k) });
  });
  Object.values(item._couple || {}).filter(isCorner).forEach((k) => {
    out.push({ title: `CORNER COUPLING ${k.own ? `${item.tag}/${k.tag}` : `${k.tag}/${item.tag}`} ${k.angle}° (PLAN)`, ...cornerDetail(item, glass, { ...k, type: "post", dir: k.dir === "int" ? "int" : "ext" }) });
  });
  return out;
}

function finalize(c, map) {
  const P = c.P.map((p) => {
    if (p.t === "rect") {
      const [ax, ay] = map(p.x, p.y), [bx, by] = map(p.x + p.w, p.y + p.h);
      return { ...p, x: Math.min(ax, bx), y: Math.min(ay, by), w: Math.abs(bx - ax), h: Math.abs(by - ay) };
    }
    if (p.t === "circle") { const [cx, cy] = map(p.cx, p.cy); return { ...p, cx, cy }; }
    if (p.t === "poly") return { ...p, pts: p.pts.map(([x, y]) => map(x, y)) };
    const [x1, y1] = map(p.x1, p.y1), [x2, y2] = map(p.x2, p.y2);
    return { ...p, x1, y1, x2, y2 };
  });
  const labels = c.L.map((l) => { const [x, y] = map(l.x, l.y); return { x, y, text: l.text }; });
  const xs = [], ys = [];
  P.forEach((p) => {
    if (p.t === "rect") { xs.push(p.x, p.x + p.w); ys.push(p.y, p.y + p.h); }
    else if (p.t === "circle") { xs.push(p.cx - p.r, p.cx + p.r); ys.push(p.cy - p.r, p.cy + p.r); }
    else if (p.t === "poly") p.pts.forEach(([x, y]) => { xs.push(x); ys.push(y); });
    else { xs.push(p.x1, p.x2); ys.push(p.y1, p.y2); }
  });
  const minX = Math.min(...xs), minY = Math.min(...ys);
  return { prims: P, labels, shapes: c.used, bounds: { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY } };
}

export function buildSectionDetails(item, panels) {
  const glass = parseGlass(item.glass_spec);
  const W = item.width, H = item.height;
  const touching = (pred) => panels.filter(pred).map((p) => p.op);
  const head = kindOf(touching((p) => p.y0 === 0));
  const sill = kindOf(touching((p) => p.y1 === H));
  const jamb = kindOf(touching((p) => p.x0 === 0));
  const out = [
    { title: "HEAD SECTION", ...edgeDetail(item, glass, head === "door" ? "sash" : head, "head") },
    { title: "SILL SECTION", ...edgeDetail(item, glass, sill, "sill") },
    { title: "JAMB SECTION (PLAN)", ...edgeDetail(item, glass, jamb === "door" ? "sash" : jamb, "jamb") },
    ...cornerSectionDetails(item),
  ];
  const cx = activeCorners(item).map((k) => k.x);
  const seen = new Set();
  coupledJoints(item).forEach((j) => {
    const k = `${j.type}-${j.orientation}`;
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ title: `COUPLING ${j.mark} · ${j.type.replace("+", " ↔ ").toUpperCase()} (${j.orientation === "v" ? "PLAN" : "VERTICAL"})`, ...couplingDetail(item, glass, j) });
  });
  if (panels.some((p) => p.x0 > 0 && p.x0 < W && !cx.includes(p.x0))) out.push({ title: "MULLION SECTION (PLAN)", ...mullionDetail(item, glass, false) });
  if (panels.some((p) => p.y0 > 0 && p.y0 < H)) out.push({ title: "TRANSOM SECTION", ...mullionDetail(item, glass, true) });
  if (panels.filter((p) => p.op.startsWith("sliding")).length > 1) out.push({ title: "INTERLOCK SECTION (PLAN)", ...interlockDetail(item, glass) });
  return out.slice(0, 6);
}
